import moment, { type Moment } from "moment";
import type {
	IAddressConstructorParams,
	IPortingDetails,
	IProductConstructorParams,
	ISimCardDetails,
} from "../interfaces";
import Address from "./address";
import Product from "./product";

const forceNull = <T>(val: T | undefined): T | null => {
	if (val === undefined) return null;
	return val;
};

interface IOrderConstructorParams {
	status: string;
	shippingType: string;
	trackingId?: string;
	deliveryCreated?: string;
	eta: {
		minDate: string;
		maxDate: string;
	};
	sim?: {
		msisdn?: string;
		iccid?: string;
	};
	deliveryAddress: IAddressConstructorParams;
	portin?: {
		accountNumber?: string;
		birthdate?: string;
		msisdn: string;
		portinType?: string;
	};
	purchasedProducts?: Array<IProductConstructorParams>;
	isActivated?: boolean;
	originType?: string;
}

export enum OrderTypeEnum {
	NEW_NUMBER = "NEW_NUMBER",
	PORT_NUMBER = "PORT_NUMBER",
}

export type OriginType = "ACTIVATION" | "SIM_SWAP";

export const OriginType: { [key: string]: OriginType } = {
	ACTIVATION: "ACTIVATION",
	SIM_SWAP: "SIM_SWAP",
};

const SIMCardTrackerStatus: Record<string, number> = {
	ORDERED: 0,
	ON_ITS_WAY: 1,
	ARRIVING: 2,
	ARRIVED: 3,
	NONE: 4,
};

class Order {
	public static SIM_ORDER_TIMEOUT_DAYS = 5;

	public static EmptyConstructorParams: IOrderConstructorParams = {
		status: "NONE",
		shippingType: "",
		trackingId: "",
		deliveryCreated: "",
		eta: {
			minDate: "",
			maxDate: "",
		},
		deliveryAddress: Address.EmptyConstructorParams,
	};

	responseData: IOrderConstructorParams;

	public static ConstructEmpty(): Order {
		return Order.Construct(Order.EmptyConstructorParams);
	}

	public static Construct(params: IOrderConstructorParams): Order {
		return new Order(params);
	}

	constructor(params: IOrderConstructorParams) {
		this.responseData = params;
	}

	get orderType(): OrderTypeEnum {
		if (this.responseData.portin === undefined) {
			return OrderTypeEnum.NEW_NUMBER;
		}

		return OrderTypeEnum.PORT_NUMBER;
	}

	get portingDetails(): IPortingDetails | null {
		if (this.responseData.portin === undefined) {
			return null;
		}

		return {
			msisdn: this.responseData.portin.msisdn,
			accountNumber: forceNull(this.responseData.portin.accountNumber),
			planType: forceNull(this.responseData.portin.portinType),
			birthdate: forceNull(this.responseData.portin.birthdate),
		};
	}

	get simCardDetails(): ISimCardDetails | null {
		if (this.responseData.sim === undefined) {
			return null;
		}

		return {
			msisdn: forceNull(this.responseData.sim.msisdn),
			iccid: forceNull(this.responseData.sim.iccid),
		};
	}

	get isPlanTypeSelected(): boolean {
		if (
			!this.portingDetails ||
			!this.portingDetails.msisdn ||
			!this.portingDetails.planType
		) {
			return false;
		}

		if (
			this.portingDetails.planType === "PREPAID" &&
			this.portingDetails.birthdate
		) {
			return true;
		}

		if (
			this.portingDetails.planType === "POSTPAID" &&
			this.portingDetails.accountNumber
		) {
			return true;
		}

		return false;
	}

	get isSimCardScanned(): boolean {
		if (
			!this.simCardDetails ||
			!this.simCardDetails.iccid ||
			!this.simCardDetails.msisdn
		) {
			return false;
		}

		return true;
	}

	get simCardTrackerStatus(): number {
		const val = SIMCardTrackerStatus[this.responseData.status];

		if (val === undefined) {
			const validOrderStatuses = Object.keys(SIMCardTrackerStatus);
			console.warn(
				"Invalid order status, must be one of",
				validOrderStatuses,
				", but got",
				this.responseData.status,
			);
			return 0;
		}

		return val;
	}

	get deliveryETA(): string | null {
		if (!this.responseData.eta) {
			return null;
		}

		return moment(this.responseData.eta.maxDate, "YYYY-MM-DD").format(
			"DD/MM/YYYY",
		);
	}

	get purchasedProducts(): Array<Product> {
		if (!this.responseData.purchasedProducts) {
			return [];
		}

		return Product.ConstructList(this.responseData.purchasedProducts);
	}

	get purchasedPlan(): Product | null {
		const res = this.purchasedProducts.find(Product.IsPlan);

		if (!res) {
			return null;
		}

		return res;
	}

	get purchasedAddons(): Array<Product> {
		return this.purchasedProducts.filter(Product.IsAddon);
	}

	get originType(): OriginType | null {
		if (
			!this.responseData ||
			!this.responseData.originType ||
			!OriginType[this.responseData.originType]
		) {
			return null;
		}

		return OriginType[this.responseData.originType];
	}

	get isActivated(): boolean {
		if (!this.responseData) {
			return false;
		}

		return !!this.responseData.isActivated;
	}

	get canActivateSim(): boolean {
		return this.originType === OriginType.SIM_SWAP && !this.isActivated;
	}
}

export default Order;
